import React, { useState, useMemo } from "react";
import ChevronIcon from "../../assets/icons/ChevronIcon.svg?react";
import useMultiSelect from "../../hooks/useMultiSelect";
import XButton from "../common/XButton";
import { cn } from "../../utils/classNames";

const wrapperClasses = "relative";

// `!` modifiers force these over XButton's `close` variant which sets
// border-0!/p-0!/bg-transparent and would otherwise camouflage the trigger.
const triggerClasses =
    "flex w-full h-10 items-center justify-between! " +
    "rounded border! border-border! bg-white! cursor-pointer " +
    "px-2!";

const labelClasses = "text-sm text-content";

export const tierSelectIconBase = "transition-transform duration-200 ease-in-out text-primary";

const dropdownClasses =
    "absolute top-[calc(100%+4px)] left-0 w-60 bg-surface " +
    "border border-border-light rounded-lg p-2 z-[1000] " +
    "shadow-[0_4px_12px_rgba(0,0,0,0.08)]";

const optionClasses =
    "flex items-center gap-2 px-1 py-1.5 text-sm cursor-pointer " +
    "hover:bg-surface-light";

const selectAllClasses = "font-medium mb-1";

const optionInputClasses = "accent-primary";

const searchInputClasses =
    "w-full mb-2 px-2 py-1 text-sm border border-border-light rounded " +
    "focus:outline-none focus:border-primary";

const emptyStateClasses = "px-1 py-1.5 text-sm text-subtle";

const errorClasses = "block mt-1 ml-1 text-sm text-danger";

const MultiSelectDropdown = ({
    value = [],
    onChange,
    options = [],
    placeholder = "Select",
    searchable = false,
    searchPlaceholder = "Search...",
    labelClassName,
    error,
}) => {

    const [searchTerm, setSearchTerm] = useState("");

    // prefix match, case-insensitive; only applied when searchable is enabled
    const filteredOptions = useMemo(() => {
        if (!searchable) return options;
        const term = searchTerm.trim().toLowerCase();
        if (!term) return options;
        return options.filter((option) =>
            String(option.text ?? "").toLowerCase().startsWith(term)
        );
    }, [options, searchTerm, searchable]);

    const {
        open,
        setOpen,
        ref,
        selectAllRef,
        allSelected,
        toggleOption,
        toggleSelectAll,
    } = useMultiSelect({
        value,
        onChange,
        options,
    });

    const displayLabel =
        value.length > 0
            ? `${placeholder} (${value.length})`
            : placeholder;

    return (
        <div className={wrapperClasses} ref={ref}>
            <XButton
                variant="close"
                className={triggerClasses}
                onClick={() => setOpen((prev) => !prev)}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className={cn(labelClasses, labelClassName)}>
                    {displayLabel}
                </span>

                <ChevronIcon
                    className={`${tierSelectIconBase} ${open ? "rotate-180" : ""}`}
                />
            </XButton>

            {open && (
                <div className={dropdownClasses}>
                    {searchable && (
                        <input
                            type="text"
                            className={searchInputClasses}
                            placeholder={searchPlaceholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            // stop clicks inside the input from closing dropdown via outside-click logic
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                        />
                    )}

                    <label className={`${optionClasses} ${selectAllClasses}`}>
                        <input
                            ref={selectAllRef}
                            type="checkbox"
                            className={optionInputClasses}
                            checked={allSelected}
                            onChange={toggleSelectAll}
                        />
                        Select All
                    </label>

                    <hr />

                    {filteredOptions.length === 0 ? (
                        <div className={emptyStateClasses}>No matches</div>
                    ) : (
                        filteredOptions.map((option) => (
                            <label
                                key={option.value}
                                className={optionClasses}
                            >
                                <input
                                    type="checkbox"
                                    className={optionInputClasses}
                                    checked={value.includes(option.value)}
                                    onChange={() => toggleOption(option.value)}
                                />
                                {option.text}
                            </label>
                        ))
                    )}
                </div>
            )}

            {error && <span className={errorClasses}>{error}</span>}
        </div>
    );
};

export default MultiSelectDropdown;
